import React, { useEffect, useState } from 'react';
import { Table, Popover, Button, Row, Image, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import * as moment from 'moment';
import { MainLayout } from '../../components/Layout';
import { Admin } from '../../queries';
import { getApolloClient } from '../../clients/apollo';

const { Text } = Typography;
const PAYMENT_METHODS_TYPES = {
  HYPER_PAY: 'Hyperpay',
};

const USER_ACCOUNT_TYPES = {
  INDIVIDUAL: 'Individual',
  REAL_ESTATE_OFFICE: 'Real Estate Office',
  DEVELOPMENT_COMPANY: 'Development Company',
  COMMERCIAL_COMPANY: 'Commercial Company',
};

const ORDER_TYPES = {
  CAMPAIGN: 'Campaign',
  BUNDLE: 'Bundle',
};

const CAMPAIGN_TYPES = {
  PRIME: 'Prime',
  FEATURED: 'Featured',
  ON_FACEBOOK: 'On Facebook',
};

const TRANSACTION_STATUS_TYPES = {
  SUCCESS: { value: 'Success', color: '#52c41a' },
  FAIL: { value: 'Fail', color: '#faad14' },
  PENDING: { value: 'Pending', color: '#6e085c' },
};

let client = null;

export default function Orders(props) {
  const [isLoading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    position: ['topLeft', 'bottomLeft'],
  });

  const getOrders = async ({
    where = {},
    orderBy = { createdAt: 'desc' },
    skip = 0,
    take = 10,
    newPagination,
  }) => {
    try {
      setLoading(true);

      const ordersResponse = await client.query({
        query: Admin.Order.GET_ORDERS,
        variables: {
          where,
          orderBy,
          skip,
          take,
        },
      });

      const ordersCount = await client.query({
        query: Admin.Order.GET_ORDERS_COUNT,
        variables: {
          where,
        },
      });

      setPagination({
        ...newPagination,
        total: ordersCount.data.ordersConnection._count.id,
      });

      setOrders(ordersResponse.data.orders);
    } catch (error) {
      console.log('🚀 ~ file: orders.js ~ line 65 ~ getOrders ~ error', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (currentPagination, filters, sorter) => {
    const where = {};
    let orderBy = {};
    if (filters.category) {
      where.category = {
        in: filters.category,
      };
    }

    if (filters.source) {
      where.source = {
        equals: filters.source,
      };
    }

    if (filters.status) {
      where.status = {
        in: filters.status,
      };
    }

    if (sorter.field && sorter.order) {
      orderBy[sorter.field] = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      orderBy = {
        createdAt: 'desc',
      };
    }

    const skip = (currentPagination.current - 1) * currentPagination.pageSize;

    const newPagination = {
      ...pagination,
      ...currentPagination,
    };
    await getOrders({ where, orderBy, skip, newPagination });
  };

  useEffect(() => {
    const { token } = props;
    client = getApolloClient({ token });
    getOrders({});
  }, []);

  const paymentMethodPopOver = ({ paymentMethod }) => {
    return (
      <Table
        key={`${paymentMethod.id}-payment-method-pop-over`}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name-paymentMethod',
          },
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type-paymentMethod',
            render: (type) => PAYMENT_METHODS_TYPES[type],
          },
          {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'status-paymentMethod',
            render: (isActive) => (isActive ? 'Enabled' : 'Disabled'),
          },
          {
            title: 'Id',
            dataIndex: 'id',
            key: 'id-paymentMethod',
          },
        ]}
        dataSource={[paymentMethod]}
        size="small"
        pagination={false}
        bordered
        rowKey={(record) => `payment-method-pop-over-${record.id}`}
      />
    );
  };

  const userPopOver = ({ user }) => {
    return (
      <Table
        key={`${user.id}-user-pop-over`}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name-user',
          },
          {
            title: 'Account Type',
            dataIndex: 'accountType',
            key: 'accountType-user',
            render: (accountType) => USER_ACCOUNT_TYPES[accountType],
          },
          {
            title: 'Photo',
            dataIndex: 'photo',
            key: 'photo-user',
            render: (photo) => {
              return (
                <Image
                  preview={false}
                  width={200}
                  height={200}
                  src={`${process.env.REACT_APP_IMAGES_BASE_URL}/users/${user.id}/${photo}`}
                  fallback="data:image/png;base64,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"
                />
              );
            },
          },
          {
            title: 'Role',
            dataIndex: 'role',
            key: 'role-user',
            render: (role) => (role === 'ADMIN' ? 'Admin' : 'User'),
          },
          {
            title: 'Id',
            dataIndex: 'id',
            key: 'id-user',
          },
        ]}
        dataSource={[user]}
        size="small"
        pagination={false}
        bordered
        rowKey={(record) => `user-pop-over-${record.id}`}
      />
    );
  };

  const orderedBundlePopOver = ({ orderedBundle }) => {
    return (
      <Table
        key={`${orderedBundle.id}-orderedBundle-pop-over`}
        columns={[
          {
            title: 'Price',
            dataIndex: ['bundle', 'price'],
            key: 'price-orderedBundle',
            render: (price) => `JOD${price}`,
          },
          {
            title: 'Status',
            dataIndex: ['bundle', 'isActive'],
            key: 'status-orderedBundle',
            render: (isActive) => (isActive ? 'Enabled' : 'Disabled'),
          },
          {
            title: 'Create Limit',
            dataIndex: ['bundle', 'listingsCreateLimit'],
            key: 'create-limit-orderedBundle',
            render: (listingsCreateLimit) => `${listingsCreateLimit} listings`,
          },
          {
            title: 'Ordered Bundle Id',
            dataIndex: 'id',
            key: 'id-orderedBundle',
          },
          {
            title: 'Bundle Id',
            dataIndex: ['bundle', 'id'],
            key: 'id-orderedBundle',
          },
        ]}
        dataSource={[orderedBundle]}
        size="small"
        pagination={false}
        bordered
        rowKey={(record) => `orderedBundle-pop-over-${record.id}`}
      />
    );
  };

  const orderedCampaignPopOver = ({ orderedCampaign }) => {
    return (
      <Table
        key={`${orderedCampaign.id}-orderedCampaign-pop-over`}
        columns={[
          {
            title: 'Type',
            dataIndex: ['campaign', 'type'],
            key: 'type-orderedCampaign',
            render: (type) => CAMPAIGN_TYPES[type],
          },
          {
            title: 'Price',
            dataIndex: ['campaign', 'price'],
            key: 'price-orderedCampaign',
            render: (price) => `JOD${price}`,
          },
          {
            title: 'Status',
            dataIndex: ['campaign', 'isActive'],
            key: 'status-orderedCampaign',
            render: (isActive) => (isActive ? 'Enabled' : 'Disabled'),
          },
          {
            title: 'Valid For Days',
            dataIndex: ['campaign', 'validForDays'],
            key: 'valid-for-days-orderedCampaign',
            render: (validForDays) => `${validForDays} days`,
          },
          {
            title: 'Description',
            dataIndex: ['campaign', 'description'],
            key: 'description-orderedCampaign',
          },
          {
            title: 'Ordered Campaign Id',
            dataIndex: 'id',
            key: 'id-orderedCampaign',
          },
          {
            title: 'Campaign Id',
            dataIndex: ['campaign', 'id'],
            key: 'id-orderedCampaign',
          },
        ]}
        dataSource={[orderedCampaign]}
        size="small"
        pagination={false}
        bordered
        rowKey={(record) => `orderedCampaign-pop-over-${record.id}`}
      />
    );
  };

  const transactionPopOver = ({ transaction }) => {
    return (
      <Table
        key={`${transaction.id}-transaction-pop-over`}
        columns={[
          {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount-transaction',
            render: (amount) => `JOD${amount}`,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status-transaction',
            render: (status) => (
              <Text
                style={{
                  color: TRANSACTION_STATUS_TYPES[status].color,
                }}
              >
                {TRANSACTION_STATUS_TYPES[status].value}
              </Text>
            ),
          },
          {
            title: 'Id',
            dataIndex: 'id',
            key: 'id-transaction',
          },
        ]}
        dataSource={[transaction]}
        size="small"
        pagination={false}
        bordered
        rowKey={(record) => `transaction-pop-over-${record.id}`}
      />
    );
  };
  return (
    <MainLayout title="Orders">
      <Row>
        <Button
          type="primary"
          onClick={() => {
            getOrders({});
          }}
          icon={<ReloadOutlined />}
        >
          Reload
        </Button>
      </Row>
      <Table
        columns={[
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type-order',
            render: (type) => ORDER_TYPES[type],
          },
          {
            title: 'Total Price',
            dataIndex: 'totalPrice',
            key: 'totalPrice-order',
            render: (totalPrice) => `JOD${totalPrice}`,
          },
          {
            title: 'Status',
            dataIndex: 'isDeleted',
            key: 'isDeleted',
            render: (isDeleted) => {
              return (
                <Text
                  style={{
                    color: isDeleted ? '#faad14' : '#52c41a',
                  }}
                >
                  {isDeleted ? 'Declined' : 'Accepted'}
                </Text>
              );
            },

            sorter: (a, b) => a.isDeleted - b.isDeleted,
            filters: [
              {
                text: 'Yes',
                value: true,
              },
              {
                text: 'No',
                value: false,
              },
            ],
            filterMultiple: false,
            onFilter: (value, row) => row.isDeleted === value,
          },
          {
            title: 'Listing Number',
            key: 'listing-number',
            render: (row) =>
              row.type === 'CAMPAIGN' &&
              row.orderedCampaigns &&
              row.orderedCampaigns[0] &&
              row.orderedCampaigns[0].listing &&
              row.orderedCampaigns[0].listing.no,
          },
          {
            title: 'Commercial Ad Number',
            key: 'commercial-ad-number',
            render: (row) =>
              row.type === 'CAMPAIGN' &&
              row.orderedCampaigns &&
              row.orderedCampaigns[0] &&
              row.orderedCampaigns[0].commercialAd &&
              row.orderedCampaigns[0].commercialAd.no,
          },
          {
            title: 'User Name',
            dataIndex: 'user',
            key: 'user-name',
            render: (user) => user.name,
          },
          {
            title: 'Ordered Bundle',
            dataIndex: 'orderedBundle',
            key: 'orderedBundle-order',
            render: (orderedBundle) => {
              return orderedBundle ? (
                <Row
                  style={{
                    justifyContent: 'center',
                  }}
                >
                  <Popover
                    key={`${orderedBundle.id}-ordered-bundle-pop-over-main`}
                    content={orderedBundlePopOver({ orderedBundle })}
                    title="Ordered Bundle Info"
                    trigger="click"
                    arrowPointAtCenter
                    placement="bottom"
                  >
                    <Button>Show</Button>
                  </Popover>
                </Row>
              ) : (
                ''
              );
            },
          },
          {
            title: 'Ordered Campaigns',
            dataIndex: 'orderedCampaigns',
            key: 'orderedCampaign-order',
            render: (orderedCampaigns) => {
              return orderedCampaigns.length > 0
                ? orderedCampaigns.map((orderedCampaign) => (
                    <Row
                      style={{
                        justifyContent: 'center',
                      }}
                      key={orderedCampaign.id}
                    >
                      <Popover
                        key={`${orderedCampaign.id}-ordered-campaign-pop-over-main`}
                        content={orderedCampaignPopOver({ orderedCampaign })}
                        title="Ordered Campaign Info"
                        trigger="click"
                        arrowPointAtCenter
                        placement="bottom"
                      >
                        <Button>Show</Button>
                      </Popover>
                    </Row>
                  ))
                : '';
            },
          },
          {
            title: 'User',
            dataIndex: 'user',
            key: 'user-order',
            render: (user) => {
              return (
                <Row
                  style={{
                    justifyContent: 'center',
                  }}
                >
                  <Popover
                    key={`${user.id}-user-pop-over-main`}
                    content={userPopOver({ user })}
                    title="User Info"
                    trigger="click"
                    arrowPointAtCenter
                    placement="bottom"
                  >
                    <Button>Show</Button>
                  </Popover>
                </Row>
              );
            },
          },
          {
            title: 'Payment Method',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod-order',
            render: (paymentMethod) => {
              return (
                <Row
                  style={{
                    justifyContent: 'center',
                  }}
                >
                  <Popover
                    key={`${paymentMethod.id}-payment-method-pop-over-main`}
                    content={paymentMethodPopOver({ paymentMethod })}
                    title="Payment Method Info"
                    trigger="click"
                    arrowPointAtCenter
                    placement="bottom"
                  >
                    <Button>Show</Button>
                  </Popover>
                </Row>
              );
            },
          },
          {
            title: 'Transactions',
            dataIndex: 'transactions',
            key: 'transaction-order',
            render: (transactions) => {
              return transactions.length > 0
                ? transactions.map((transaction) => (
                    <Row
                      style={{
                        justifyContent: 'center',
                      }}
                      key={transaction.id}
                    >
                      <Popover
                        key={`${transaction.id}-transaction-pop-over-main`}
                        content={transactionPopOver({ transaction })}
                        title="Transaction Info"
                        trigger="click"
                        arrowPointAtCenter
                        placement="bottom"
                      >
                        <Button>Show</Button>
                      </Popover>
                    </Row>
                  ))
                : '';
            },
          },
          {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt) => moment(createdAt).format('L'),
            sorter: (a, b) => {
              if (!a.createdAt || !b.createdAt) return false;
              const diff = moment(a.createdAt).diff(
                moment(b.createdAt),
                'seconds'
              );
              return diff;
            },
          },
          {
            title: 'Updated at',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (updatedAt) => moment(updatedAt).format('L'),
            sorter: (a, b) => {
              if (!a.updatedAt || !b.updatedAt) return false;
              const diff = moment(a.updatedAt).diff(
                moment(b.updatedAt),
                'seconds'
              );
              return diff;
            },
          },
          {
            title: 'Id',
            dataIndex: 'id',
            key: 'id-order',
          },
        ]}
        dataSource={orders}
        bordered
        rowKey={(record) => `orders-${record.id}`}
        loading={isLoading}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </MainLayout>
  );
}
